import { db, backupDb } from './data_oscbase';
import { defaultActivityRoutes } from './config/activity-routes.config';

// Cache pour les données de test
let testDataCache = {};

// Fonction pour charger les données de test depuis le dossier public (format JSON)
const loadTestDataFromPublic = async (activityId) => {
  if (testDataCache[activityId]) {
    console.log(`📦 Données ${activityId} récupérées du cache`);
    return testDataCache[activityId];
  }

  try {
    console.log(`🔄 Chargement des données pour ${activityId} (JSON)...`);
    const response = await fetch(`/testData/${activityId}.json`);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: Impossible de charger /testData/${activityId}.json`);
    }
    const data = await response.json();
    console.log(`✅ Données ${activityId} chargées avec succès:`, {
      products: data.products?.length || 0,
      categories: data.categories?.length || 0,
      customers: data.customers?.length || 0,
      tables: data.tables?.length || 0
    });
    testDataCache[activityId] = data;
    return data;
  } catch (error) {
    console.error(`❌ Erreur lors du chargement des données pour ${activityId}:`, error);
    return null;
  }
};

class TestDataManager {
  // Liste des tables à sauvegarder/restaurer
  static getBackupTables() {
    return [
      'categories',
      'products',
      'customers',
      'settings',
      'sales',
      'payments',
      'approvisionnements',
      'modelesVente',
      'champsCustomValues',
      'tables',
      'appointments',
      'commandes',
      'activitySettings'
    ];
  }

  static async getAvailableActivities() {
    const activities = [
      { id: 'restaurant', name: 'Restaurant' },
      { id: 'boulangerie', name: 'Boulangerie' },
      { id: 'coiffure', name: 'Salon de Coiffure' },
      { id: 'autoecole', name: 'Auto-École' },
      { id: 'vetements', name: 'Magasin de Vêtements' },
      { id: 'reparation', name: 'Service de Réparation' },
      { id: 'electronique', name: 'Accessoires Électroniques' },
      { id: 'epicerie', name: 'Épicerie' },
      { id: 'atelier', name: 'Atelier' }
    ];

    // Charger les données pour chaque activité
    const activitiesWithData = [];
    for (const activity of activities) {
      const data = await loadTestDataFromPublic(activity.id);
      if (data) {
        activitiesWithData.push({
          ...activity,
          data: data
        });
      }
    }

    return activitiesWithData;
  }

  static async initializeBackupData() {
    try {
      const existingData = await backupDb.activityData.toArray();
      if (existingData.length === 0) {
        const activities = await this.getAvailableActivities();
        for (const activity of activities) {
          if (!activity.data) continue; // Ignorer les activités sans données

          // Préparer les settings avec l'activité courante
          const activitySettings = [
            ...(activity.data.settings || []),
            {
              key: 'current_activity',
              value: activity.id,
              type: 'string',
              description: 'Activité actuellement sélectionnée',
              isSystem: true,
              createdAt: new Date(),
              updatedAt: new Date()
            }
          ];

          const activityBackupData = {
            categories: activity.data.categories || [],
            products: activity.data.products || [],
            customers: activity.data.customers || [],
            settings: activitySettings,
            sales: activity.data.sales || [],
            payments: activity.data.payments || [],
            approvisionnements: activity.data.approvisionnements || [],
            modelesVente: activity.data.modelesVente || [],
            champsCustomValues: activity.data.champsCustomValues || [],
            tables: activity.data.tables || [],
            appointments: activity.data.appointments || [],
            commandes: activity.data.commandes || [],
            // activitySettings doit être un objet unique, pas un tableau
            activitySettings: activity.data.activitySettings || null
          };

          await backupDb.activityData.add({
            activityId: activity.id,
            name: activity.name,
            data: activityBackupData,
            lastUpdated: new Date()
          });
        }
      }
    } catch (error) {
      console.error('Erreur lors de l\'initialisation des données de sauvegarde:', error);
      throw error;
    }
  }

  static async verifyBackupData() {
    try {
      const activities = await backupDb.activityData.toArray();
      console.log('Données sauvegardées pour chaque activité:');
      for (const activity of activities) {
        console.log(`\nActivité: ${activity.name}`);
        console.log('Produits:', activity.data.products.map(p => p.name));
        console.log('Catégories:', activity.data.categories.map(c => c.name));
        if (activity.data.tables) console.log('Tables:', activity.data.tables.map(t => t.number));
        if (activity.data.appointments) console.log('Rendez-vous:', activity.data.appointments.length);
        if (activity.data.commandes) console.log('Commandes:', activity.data.commandes.length);
        if (activity.data.activitySettings) console.log('activitySettings:', activity.data.activitySettings);
      }
      return activities;
    } catch (error) {
      console.error('Erreur lors de la vérification des données:', error);
      throw error;
    }
  }

  static async saveCurrentActivityData(activityId) {
    try {
      // Récupérer toutes les données actuelles
      const backupData = {};
      for (const table of this.getBackupTables()) {
        if (table === 'activitySettings') {
          // Récupérer activitySettings comme un objet unique
          const activitySettings = await db[table].toArray();
          backupData[table] = activitySettings.length > 0 ? activitySettings[0] : null;
        } else {
          // Récupérer les autres tables comme des tableaux
          backupData[table] = await db[table].toArray();
        }
      }

      // Mettre à jour les données de l'activité
      await backupDb.activityData.put({
        activityId,
        name: (await this.getAvailableActivities()).find(a => a.id === activityId)?.name || activityId,
        data: backupData,
        lastUpdated: new Date()
      });

      console.log(`Données de l'activité ${activityId} sauvegardées`);
      return true;
    } catch (error) {
      console.error('Erreur lors de la sauvegarde des données:', error);
      throw error;
    }
  }

  static async loadActivityData(activityId) {
    try {
      // Récupérer les données de l'activité depuis la sauvegarde
      const activityData = await backupDb.activityData.get(activityId);
      if (!activityData) {
        throw new Error(`Données non trouvées pour l'activité: ${activityId}`);
      }

      // Sauvegarder les données actuelles dans la sauvegarde
      const currentActivityId = await this.getCurrentActivityId();
      if (currentActivityId) {
        const backupData = {};
        for (const table of this.getBackupTables()) {
          try {
            if (table === 'activitySettings') {
              // Récupérer activitySettings comme un objet unique
              const activitySettings = await db.table(table).toArray();
              backupData[table] = activitySettings.length > 0 ? activitySettings[0] : null;
            } else {
              // Récupérer les autres tables comme des tableaux
              backupData[table] = await db.table(table).toArray();
            }
          } catch (error) {
            console.error(`Erreur lors de la sauvegarde de la table ${table}:`, error);
          }
        }

        await backupDb.activityData.put({
          activityId: currentActivityId,
          name: (await this.getAvailableActivities()).find(a => a.id === currentActivityId)?.name || currentActivityId,
          data: backupData,
          lastUpdated: new Date()
        });
      }

      // Vider toutes les tables de la base principale
      for (const table of this.getBackupTables()) {
        try {
          await db.table(table).clear();
        } catch (error) {
          console.error(`Erreur lors du vidage de la table ${table}:`, error);
        }
      }

      // Restaurer les données de la nouvelle activité
      const { data } = activityData;
      for (const [table, records] of Object.entries(data)) {
        if (records) {
          try {
            if (table === 'activitySettings') {
              // Traiter activitySettings comme un objet unique
              if (records) {
                await db.table(table).add(records);
              }
            } else {
              // Traiter les autres tables comme des tableaux
              if (Array.isArray(records) && records.length > 0) {
                await db.table(table).bulkAdd(records);
              }
            }
          } catch (error) {
            console.error(`Erreur lors de la restauration de la table ${table}:`, error);
          }
        }
      }

      console.log(`Données de l'activité ${activityId} chargées avec succès`);
      return true;
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
      throw error;
    }
  }

  static async getCurrentActivityId() {
    try {
      const setting = await db.settings.where('key').equals('current_activity').first();
      return setting ? setting.value : null;
    } catch (error) {
      console.error('Erreur lors de la récupération de l\'activité courante:', error);
      return null;
    }
  }

  static async setCurrentActivityId(activityId) {
    try {
      await db.settings.put({
        key: 'current_activity',
        value: activityId,
        type: 'string',
        description: 'Activité actuellement sélectionnée',
        isSystem: true,
        createdAt: new Date(),
        updatedAt: new Date()
      });
      console.log(`Activité courante définie: ${activityId}`);
      return true;
    } catch (error) {
      console.error('Erreur lors de la définition de l\'activité courante:', error);
      throw error;
    }
  }

  static async resetActivityData(activityId) {
    try {
      // Charger les données initiales de l'activité
      await this.loadActivityData(activityId);
      console.log(`Données de l'activité ${activityId} réinitialisées`);
      return true;
    } catch (error) {
      console.error('Erreur lors de la réinitialisation des données:', error);
      throw error;
    }
  }

  static async getActivityInfo(activityId) {
    try {
      const activityData = await backupDb.activityData.get(activityId);
      if (!activityData) {
        throw new Error(`Activité non trouvée: ${activityId}`);
      }
      return {
        id: activityData.activityId,
        name: activityData.name,
        lastUpdated: activityData.lastUpdated,
        dataCount: {
          products: activityData.data.products?.length || 0,
          categories: activityData.data.categories?.length || 0,
          customers: activityData.data.customers?.length || 0,
          sales: activityData.data.sales?.length || 0
        }
      };
    } catch (error) {
      console.error('Erreur lors de la récupération des informations de l\'activité:', error);
      throw error;
    }
  }

  static async resetBackupDatabase() {
    try {
      await backupDb.activityData.clear();
      await this.initializeBackupData();
      console.log('Base de données de sauvegarde réinitialisée');
      return true;
    } catch (error) {
      console.error('Erreur lors de la réinitialisation de la base de sauvegarde:', error);
      throw error;
    }
  }

  static async deleteAllDatabases() {
    try {
      await db.delete();
      await backupDb.delete();
      console.log('Toutes les bases de données supprimées');
      return true;
    } catch (error) {
      console.error('Erreur lors de la suppression des bases de données:', error);
      throw error;
    }
  }

  static async cleanDuplicateSettings() {
    try {
      const settings = await db.settings.toArray();
      const duplicates = settings.filter((setting, index, self) => 
        index !== self.findIndex(s => s.key === setting.key)
      );
      
      for (const duplicate of duplicates) {
        await db.settings.delete(duplicate.id);
      }
      
      console.log(`${duplicates.length} doublons supprimés`);
      return duplicates.length;
    } catch (error) {
      console.error('Erreur lors du nettoyage des doublons:', error);
      throw error;
    }
  }
}

export { TestDataManager }; 